import { useState, useMemo, useRef, useEffect } from "react";
import {
  Search,
  ChevronDown,
  Repeat2,
  Star,
  Eye,
  Trash2,
  X,
  Info,
  FileText,
  AlertTriangle,
  ExternalLink,
  Plus,
  ChevronUp,
  Check,
} from "lucide-react";

/* ── Types ───────────────────────────────────────────── */

interface PlaygroundCheatsheet {
  id: string;
  title: string;
  content: string;
  createdAt: string;
}

interface ReferencedFrom {
  originalId: string;
  originalTitle: string;
  originalAuthor: string;
  originalAuthorAvatar?: string;
}

interface ShareItem {
  id: string;
  cheatsheetTitle: string;
  cheatsheetContent: string;
  competitionName: string;
  publishedAt: string;
  referenceCount: number;
  favoriteCount: number;
  isOwner: boolean;
  publisherName: string;
  publisherAvatar?: string; // Optional avatar URL
  referencedFrom?: ReferencedFrom; // Optional reference information
}

/* ── Mock Playground Cheatsheets ─────────────────────── */

const PLAYGROUND_CHEATSHEETS: PlaygroundCheatsheet[] = [
  {
    id: "cs-1",
    title: "Chain-of-Thought Decomposition",
    content: `# Core Strategy: Chain-of-Thought Decomposition\n\n1. Break complex problems into atomic sub-problems\n2. Use dimensional analysis as a verification step\n3. Apply Polya's heuristic: Understand → Plan → Execute → Verify\n4. For combinatorics: always double-check overcounting via inclusion-exclusion\n5. For number theory: check small cases (n=1,2,3) before generalizing\n6. Template: "Let x = ... Suppose for contradiction that ..."`,
    createdAt: "2026-03-10T08:00:00Z",
  },
  {
    id: "cs-2",
    title: "Prompt Engineering for Math Reasoning",
    content: `## Prompt Engineering for Math Reasoning\n\nKey rules:\n- Always ask the model to show intermediate steps\n- Use "Verify your answer by substituting back"\n- For geometry: "Draw a diagram mentally and label all vertices"\n- For algebra: "Factor before expanding"\n- Avoid leading the model toward a specific answer\n- Temperature 0.2 works best for deterministic math`,
    createdAt: "2026-03-09T14:30:00Z",
  },
  {
    id: "cs-3",
    title: "Competition Optimization Notes",
    content: `### Competition Optimization Notes\n\n1. Model selection matters: o3 > Claude 3.5 Sonnet > GPT-4o for competition math\n2. Cost optimization: use smaller models for easy problems, reserve o3 for hard ones\n3. Cheatsheet structure: problem-type → strategy → verification method\n4. Critical: always include "If stuck, try a different approach" as a fallback\n5. For inequalities: AM-GM, Cauchy-Schwarz, Jensen's — try in that order\n6. For sequences: check if telescoping works before attempting closed form`,
    createdAt: "2026-03-08T10:00:00Z",
  },
  {
    id: "cs-4",
    title: "Proof Problem Strategies",
    content: `# Distillation Cheatsheet — Focused on Proof Problems\n\nStructure each prompt as:\n1. "State the problem clearly"\n2. "Identify what type of proof is needed (direct, contradiction, induction)"\n3. "Write the proof skeleton before filling in details"\n4. "Check each logical step for gaps"\n\nFor induction specifically:\n- Base case: always verify n=0 AND n=1\n- Inductive step: clearly state "Assume P(k) holds for some k ≥ 1"\n- Conclusion: explicitly state "By mathematical induction, P(n) holds for all n ≥ 0"`,
    createdAt: "2026-03-07T16:00:00Z",
  },
  {
    id: "cs-5",
    title: "Error Analysis Framework",
    content: `## Error Analysis Framework\n\nWhen the model gets an answer wrong:\n1. Was the problem understood correctly? → Rephrase and retry\n2. Was the approach correct but execution flawed? → Add "double-check arithmetic"\n3. Was the approach fundamentally wrong? → Add domain-specific hint\n4. Was it a formatting issue? → Standardize output format\n\nCommon failure modes:\n- Sign errors in complex arithmetic\n- Forgetting edge cases (n=0, empty set)\n- Confusing necessary vs sufficient conditions\n- Off-by-one errors in counting problems`,
    createdAt: "2026-03-06T12:00:00Z",
  },
  {
    id: "cs-6",
    title: "Speed-Optimized Lightweight Cheatsheet",
    content: `# Lightweight Cheatsheet for Speed\n\nPrioritize:\n- Recognize problem patterns fast (< 5 sec classification)\n- Use templates: "This is a [type] problem. Apply [method]."\n- Skip verification on easy problems to save tokens\n- Reserve full CoT for problems scoring > 0.6 difficulty\n\nQuick patterns:\n- "Find all x such that..." → algebraic manipulation\n- "Prove that for all n..." → induction\n- "How many ways..." → combinatorics\n- "Find the maximum/minimum..." → calculus or AM-GM`,
    createdAt: "2026-03-05T09:00:00Z",
  },
  {
    id: "cs-7",
    title: "Algebraic Manipulation Toolkit",
    content: `## Algebraic Manipulation Toolkit\n\nCore techniques:\n- Factor first, expand later\n- Complete the square for quadratics\n- Substitution: let u = complex expression\n- Polynomial long division for rational functions\n- Vieta's formulas for symmetric functions\n\nCommon mistakes to avoid:\n- Dividing both sides by an expression that could be zero\n- Squaring both sides without checking for extraneous solutions\n- Dropping negative signs during distribution`,
    createdAt: "2026-03-04T14:20:00Z",
  },
  {
    id: "cs-8",
    title: "Geometric Reasoning Framework",
    content: `# Geometric Reasoning Framework\n\nApproach:\n1. Draw a clear diagram with all given information\n2. Label all points, angles, and lengths\n3. Identify congruent/similar triangles\n4. Use angle chasing systematically\n5. Apply Pythagorean theorem when right angles present\n\nKey theorems:\n- Inscribed angle theorem\n- Power of a point\n- Similar triangles (AA, SAS, SSS)\n- Circle theorems (tangent-chord angles)`,
    createdAt: "2026-03-03T11:15:00Z",
  },
  {
    id: "cs-9",
    title: "Number Theory Essentials",
    content: `## Number Theory Essentials\n\nFundamental concepts:\n- Divisibility rules (2, 3, 5, 9, 11)\n- GCD and LCM via Euclidean algorithm\n- Modular arithmetic properties\n- Fermat's Little Theorem for primes\n- Chinese Remainder Theorem\n\nProblem-solving strategy:\n- For divisibility: use modular arithmetic\n- For primes: check small cases, look for patterns\n- For Diophantine equations: find one solution, then general form`,
    createdAt: "2026-03-02T09:30:00Z",
  },
  {
    id: "cs-10",
    title: "Calculus Problem Patterns",
    content: `# Calculus Problem Patterns\n\nDerivatives:\n- Related rates: draw diagram, identify variables, differentiate implicitly\n- Optimization: find critical points, check endpoints and second derivative\n- Mean Value Theorem applications\n\nIntegrals:\n- Substitution: look for chain rule patterns\n- Integration by parts: LIATE rule (Log, Inverse trig, Algebraic, Trig, Exponential)\n- Partial fractions for rational functions\n- Geometric/physical interpretations`,
    createdAt: "2026-03-01T16:45:00Z",
  },
  {
    id: "cs-11",
    title: "Combinatorics Checklist",
    content: `## Combinatorics Checklist\n\nCounting principles:\n- Addition principle: OR means add\n- Multiplication principle: AND means multiply\n- Permutations vs combinations: order matters?\n- Stars and bars for distributing identical objects\n- Inclusion-exclusion for overlapping sets\n\nAvoid common errors:\n- Overcounting due to symmetry\n- Forgetting the "at least one" vs "exactly one" distinction\n- Misapplying combinations when order matters`,
    createdAt: "2026-02-28T13:00:00Z",
  },
  {
    id: "cs-12",
    title: "Inequality Techniques",
    content: `# Inequality Techniques\n\nStandard approaches:\n1. AM-GM inequality: (a+b)/2 ≥ √(ab)\n2. Cauchy-Schwarz inequality\n3. Jensen's inequality for convex/concave functions\n4. Rearrangement inequality\n5. Chebyshev's inequality\n\nGeneral strategy:\n- Normalize if possible (set sum = 1)\n- Look for homogeneity\n- Try substitution: a = x², b = y²\n- Check equality case to verify correctness`,
    createdAt: "2026-02-27T10:20:00Z",
  },
];

/* ── Mock Data ───────────────────────────────────────── */

const MOCK_ITEMS: ShareItem[] = [
  {
    id: "1",
    cheatsheetTitle: "Chain-of-Thought Decomposition",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[0].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-14T10:30:00Z",
    referenceCount: 24,
    favoriteCount: 12,
    isOwner: true,
    publisherName: "Alice Johnson",
    publisherAvatar:
      "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "2",
    cheatsheetTitle: "Prompt Engineering for Math Reasoning",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[1].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-13T15:45:00Z",
    referenceCount: 18,
    favoriteCount: 9,
    isOwner: false,
    publisherName: "Bob Smith",
    publisherAvatar:
      "https://images.unsplash.com/photo-1554765345-6ad6a5417cde?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "3",
    cheatsheetTitle:
      "Competition Optimization Notes (Enhanced)",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[2].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-12T09:00:00Z",
    referenceCount: 31,
    favoriteCount: 19,
    isOwner: false,
    publisherName: "Charlie Brown",
    publisherAvatar:
      "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlcnNvbiUyMGhlYWRzaG90fGVufDF8fHx8MTc3MzY5NTkwN3ww&ixlib=rb-4.1.0&q=80&w=1080",
    referencedFrom: {
      originalId: "2",
      originalTitle: "Prompt Engineering for Math Reasoning",
      originalAuthor: "Bob Smith",
      originalAuthorAvatar:
        "https://images.unsplash.com/photo-1554765345-6ad6a5417cde?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
  },
  {
    id: "4",
    cheatsheetTitle: "Proof Problem Strategies",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[3].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-11T20:15:00Z",
    referenceCount: 15,
    favoriteCount: 7,
    isOwner: false,
    publisherName: "David Wilson",
    publisherAvatar:
      "https://images.unsplash.com/photo-1737575655055-e3967cbefd03?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkZXZlbG9wZXIlMjBwb3J0cmFpdCUyMHBob3RvfGVufDF8fHx8MTc3MzcxNDQ3OHww&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "5",
    cheatsheetTitle: "Error Analysis Framework",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[4].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-10T11:30:00Z",
    referenceCount: 22,
    favoriteCount: 14,
    isOwner: false,
    publisherName: "Eve Davis",
    publisherAvatar:
      "https://images.unsplash.com/photo-1678542230173-8e2c3eb87c85?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY2ODYzNXww&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "6",
    cheatsheetTitle: "Speed-Optimized Lightweight Cheatsheet",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[5].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-09T16:00:00Z",
    referenceCount: 11,
    favoriteCount: 5,
    isOwner: false,
    publisherName: "Frank Lee",
    publisherAvatar:
      "https://images.unsplash.com/photo-1706025090794-7ade2c1b6208?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMHByb2Zlc3Npb25hbCUyMGhlYWRzaG90fGVufDF8fHx8MTc3MzY4NDg0Nnww&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "7",
    cheatsheetTitle:
      "Algebraic Manipulation Toolkit (Modified)",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[6].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-08T19:30:00Z",
    referenceCount: 16,
    favoriteCount: 8,
    isOwner: false,
    publisherName: "Grace Martinez",
    publisherAvatar:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMGJ1c2luZXNzfGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    referencedFrom: {
      originalId: "3",
      originalTitle:
        "Competition Optimization Notes (Enhanced)",
      originalAuthor: "Charlie Brown",
      originalAuthorAvatar:
        "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlcnNvbiUyMGhlYWRzaG90fGVufDF8fHx8MTc3MzY5NTkwN3ww&ixlib=rb-4.1.0&q=80&w=1080",
    },
  },
  {
    id: "8",
    cheatsheetTitle: "Geometric Reasoning Framework",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[7].content,
    competitionName: "Mathematics Distillation v2",
    publishedAt: "2026-03-07T14:00:00Z",
    referenceCount: 27,
    favoriteCount: 15,
    isOwner: false,
    publisherName: "Henry Chen",
    publisherAvatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYW4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "9",
    cheatsheetTitle: "Number Theory Essentials",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[8].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-06T10:45:00Z",
    referenceCount: 20,
    favoriteCount: 11,
    isOwner: false,
    publisherName: "Isabella Rodriguez",
    publisherAvatar:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHdvbWFuJTIwcG9ydHJhaXR8ZW58MXx8fHwxNzczNjc3Mjk2fDA&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "10",
    cheatsheetTitle: "Calculus Problem Patterns (Extended)",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[9].content,
    competitionName: "Mathematics Distillation v2",
    publishedAt: "2026-03-05T16:20:00Z",
    referenceCount: 13,
    favoriteCount: 6,
    isOwner: false,
    publisherName: "Jack Thompson",
    publisherAvatar:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzYzNDM1MXww&ixlib=rb-4.1.0&q=80&w=1080",
    referencedFrom: {
      originalId: "8",
      originalTitle: "Geometric Reasoning Framework",
      originalAuthor: "Henry Chen",
      originalAuthorAvatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYW4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
  },
  {
    id: "11",
    cheatsheetTitle: "Combinatorics Checklist",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[10].content,
    competitionName: "Mathematics Distillation v1",
    publishedAt: "2026-03-04T12:00:00Z",
    referenceCount: 19,
    favoriteCount: 10,
    isOwner: false,
    publisherName: "Katherine Wang",
    publisherAvatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbnxlbnwxfHx8fDE3NzM2NzcyOTZ8MA&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    id: "12",
    cheatsheetTitle: "Inequality Techniques",
    cheatsheetContent: PLAYGROUND_CHEATSHEETS[11].content,
    competitionName: "Mathematics Distillation v2",
    publishedAt: "2026-03-03T08:30:00Z",
    referenceCount: 25,
    favoriteCount: 13,
    isOwner: false,
    publisherName: "Leo Anderson",
    publisherAvatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYW4lMjBwcm9mZXNzaW9uYWx8ZW58MXx8fHwxNzczNjM0MzUxfDA&ixlib=rb-4.1.0&q=80&w=1080",
  },
];

const COMPETITIONS = [
  "All Competitions",
  "Mathematics Distillation v1",
  "Mathematics Distillation v2",
];

const BORDER = "1px solid var(--brand-border)";

/* ── Helpers ──────────────────────────────────────────── */

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateLines(
  text: string,
  maxLines: number,
): { text: string; truncated: boolean } {
  const lines = text.split("\n");
  if (lines.length <= maxLines)
    return { text, truncated: false };
  return {
    text: lines.slice(0, maxLines).join("\n"),
    truncated: true,
  };
}

/* ── Sub-components ──────────────────────────────────── */

function ReferenceButton({
  referenced,
  count,
  onToggle,
  size = "sm",
}: {
  referenced: boolean;
  count: number;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[6px_10px] transition-all"
      style={{
        color: referenced
          ? "#3B0E27"
          : "var(--brand-text-opacity-40)",
      }}
    >
      <Repeat2
        size={iconSize}
        fill={referenced ? "#3B0E27" : "none"}
        stroke={referenced ? "#3B0E27" : "currentColor"}
        strokeWidth={1.8}
      />
      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">
        {count}
      </span>
    </button>
  );
}

function FavoriteButton({
  saved,
  count,
  onToggle,
  size = "sm",
}: {
  saved: boolean;
  count: number;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[6px_10px] transition-all"
      style={{
        color: saved
          ? "#3B0E27"
          : "var(--brand-text-opacity-40)",
      }}
    >
      <Star
        size={iconSize}
        fill={saved ? "#3B0E27" : "none"}
        stroke={saved ? "#3B0E27" : "currentColor"}
        strokeWidth={1.8}
      />
      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">
        {count}
      </span>
    </button>
  );
}

function ModalOverlay({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-[24px]"
      style={{ backgroundColor: "var(--brand-overlay)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] flex flex-col"
        style={{
          backgroundColor: "var(--brand-bg)",
          border: BORDER,
          height: "680px",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Cheatsheet Selector Dropdown ───────────────────── */

function CheatsheetSelector({
  selected,
  onSelect,
}: {
  selected: PlaygroundCheatsheet | null;
  onSelect: (cs: PlaygroundCheatsheet | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () =>
      document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-[12px] py-[10px] bg-transparent cursor-pointer font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
        style={{
          border: BORDER,
          color: selected
            ? "var(--brand-text)"
            : "var(--brand-text-opacity-40)",
          backgroundColor: "var(--brand-surface-muted)",
        }}
      >
        <span className="truncate">
          {selected
            ? selected.title
            : "Select a cheatsheet from Playground..."}
        </span>
        <ChevronDown
          size={16}
          className="shrink-0 ml-[8px]"
          style={{ color: "var(--brand-text-opacity-30)" }}
        />
      </button>
      {open && (
        <div
          className="absolute top-[calc(100%+4px)] left-0 right-0 z-50 py-[4px] max-h-[240px] overflow-y-auto"
          style={{
            backgroundColor: "var(--brand-bg)",
            border: BORDER,
            boxShadow: "0 4px 16px var(--brand-shadow)",
          }}
        >
          {PLAYGROUND_CHEATSHEETS.map((cs) => (
            <button
              key={cs.id}
              onClick={() => {
                onSelect(cs);
                setOpen(false);
              }}
              className="w-full bg-transparent border-none cursor-pointer text-left px-[14px] py-[10px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] transition-colors hover:bg-[rgba(59,14,39,0.03)]"
              style={{
                color:
                  selected?.id === cs.id
                    ? "var(--brand-text)"
                    : "var(--brand-text-opacity-60)",
                backgroundColor:
                  selected?.id === cs.id
                    ? "var(--brand-surface-subtle)"
                    : "transparent",
              }}
            >
              <p className="m-0 truncate">{cs.title}</p>
              <p
                className="m-0 mt-[2px] text-[11px]"
                style={{
                  color: "var(--brand-text-opacity-35)",
                }}
              >
                Created {formatDate(cs.createdAt)}
              </p>
            </button>
          ))}
          {PLAYGROUND_CHEATSHEETS.length === 0 && (
            <p
              className="px-[14px] py-[10px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0"
              style={{ color: "var(--brand-text-opacity-40)" }}
            >
              No cheatsheets found. Create one in Playground
              first.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Publish Modal ───────────────────────────────────── */

function PublishModal({
  onClose,
  onPublish,
}: {
  onClose: () => void;
  onPublish: (title: string, content: string) => void;
}) {
  const [selectedCs, setSelectedCs] =
    useState<PlaygroundCheatsheet | null>(null);

  // Reference Source states
  const [showManualAttribution, setShowManualAttribution] =
    useState(false);
  const [referenceSearch, setReferenceSearch] = useState("");
  const [selectedReference, setSelectedReference] =
    useState<ShareItem | null>(null);

  // Mock: Check if selected cheatsheet has automatic reference (from Share Board)
  const automaticReference: ReferencedFrom | null = null; // Would be populated if cheatsheet came from Share Board

  const canPublish = selectedCs !== null;

  const handlePublish = () => {
    if (!selectedCs) return;
    onPublish(selectedCs.title, selectedCs.content);
  };

  // Filter available items for reference search
  const availableReferences = useMemo(() => {
    if (!referenceSearch.trim()) return [];
    const query = referenceSearch.toLowerCase();
    return MOCK_ITEMS.filter(
      (item) =>
        item.cheatsheetTitle.toLowerCase().includes(query) ||
        item.publisherName.toLowerCase().includes(query),
    ).slice(0, 5);
  }, [referenceSearch]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-[24px]"
      style={{ backgroundColor: "var(--brand-overlay)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] flex flex-col"
        style={{
          backgroundColor: "var(--brand-bg)",
          border: BORDER,
          height: "680px",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-[24px] py-[20px] shrink-0"
          style={{ borderBottom: BORDER }}
        >
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[18px] m-0"
            style={{ color: "var(--brand-text)" }}
          >
            Publish to Share Board
          </p>
          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer p-[4px]"
          >
            <X
              size={18}
              style={{ color: "var(--brand-text-opacity-40)" }}
            />
          </button>
        </div>

        <div className="px-[24px] py-[20px] flex flex-col gap-[20px] overflow-y-auto flex-1 min-h-0">
          {/* Cheatsheet selector */}
          <div className="flex flex-col gap-[8px]">
            <label
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]"
              style={{ color: "var(--brand-text-opacity-60)" }}
            >
              Select Cheatsheet{" "}
              <span style={{ color: "#e74c6f" }}>*</span>
            </label>
            <CheatsheetSelector
              selected={selectedCs}
              onSelect={setSelectedCs}
            />
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0"
              style={{ color: "var(--brand-text-opacity-35)" }}
            >
              Choose a cheatsheet you created in Playground.
              Content cannot be edited here.
            </p>
          </div>

          {/* Cheatsheet preview (scrollable) */}
          {selectedCs && (
            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center gap-[6px]">
                <FileText
                  size={14}
                  style={{
                    color: "var(--brand-text-opacity-40)",
                  }}
                />
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0"
                  style={{
                    color: "var(--brand-text-opacity-60)",
                  }}
                >
                  Preview
                </p>
              </div>
              <div
                className="p-[14px] font-['Menlo,Monaco,monospace'] whitespace-pre-wrap break-words leading-[1.65] text-[13px] max-h-[240px] overflow-y-auto"
                style={{
                  backgroundColor: "var(--brand-surface-muted)",
                  border: BORDER,
                  color: "var(--brand-text)",
                }}
              >
                {selectedCs.content}
              </div>
            </div>
          )}

          {/* Reference Source Section */}
          {selectedCs && (
            <div className="flex flex-col gap-[12px]">
              <div className="flex items-center gap-[6px]">
                <Repeat2
                  size={14}
                  style={{
                    color: "var(--brand-text-opacity-40)",
                  }}
                />
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0"
                  style={{
                    color: "var(--brand-text-opacity-60)",
                  }}
                >
                  Reference Source
                </p>
              </div>

              {/* Automatic Reference (if exists) */}
              {automaticReference ? (
                <div
                  className="flex flex-col gap-[10px] p-[14px]"
                  style={{
                    backgroundColor:
                      "var(--brand-surface-muted)",
                    border: BORDER,
                  }}
                >
                  <p
                    className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px] m-0"
                    style={{
                      color: "var(--brand-text-opacity-60)",
                    }}
                  >
                    Automatic Reference
                  </p>
                  <div className="flex items-center justify-between gap-[12px]">
                    <div className="flex items-center gap-[8px] flex-1 min-w-0">
                      {automaticReference.originalAuthorAvatar && (
                        <img
                          src={
                            automaticReference.originalAuthorAvatar
                          }
                          alt={
                            automaticReference.originalAuthor
                          }
                          className="w-[20px] h-[20px] rounded-full object-cover shrink-0"
                          style={{
                            border:
                              "1px solid var(--brand-border)",
                          }}
                        />
                      )}
                      <div className="flex flex-col gap-[2px] min-w-0 flex-1">
                        <p
                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0 truncate"
                          style={{ color: "var(--brand-text)" }}
                        >
                          {automaticReference.originalTitle}
                        </p>
                        <p
                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] m-0"
                          style={{
                            color:
                              "var(--brand-text-opacity-40)",
                          }}
                        >
                          {automaticReference.originalAuthor} ·
                          Published at 2026-03-12 14:30 UTC
                        </p>
                      </div>
                    </div>
                    <button
                      className="flex items-center gap-[4px] bg-transparent border-none cursor-pointer p-[4px_8px] shrink-0"
                      style={{
                        color: "var(--brand-text-opacity-50)",
                      }}
                    >
                      <ExternalLink size={12} />
                      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px]">
                        View Original Item
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                /* Manual Attribution Section */
                <div className="flex flex-col gap-[12px]">
                  {/* Toggle button */}
                  {!showManualAttribution && (
                    <button
                      onClick={() =>
                        setShowManualAttribution(true)
                      }
                      className="flex items-center gap-[6px] bg-transparent cursor-pointer p-[10px_12px] self-start"
                      style={{
                        border: BORDER,
                        color: "var(--brand-text-opacity-60)",
                      }}
                    >
                      <Plus size={14} />
                      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">
                        Add manual attribution
                      </span>
                    </button>
                  )}

                  {/* Expanded manual attribution */}
                  {showManualAttribution && (
                    <div
                      className="flex flex-col gap-[12px] p-[14px]"
                      style={{
                        backgroundColor:
                          "var(--brand-surface-muted)",
                        border: BORDER,
                      }}
                    >
                      {/* Header with collapse button */}
                      <div className="flex items-center justify-between">
                        <p
                          className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px] m-0"
                          style={{
                            color:
                              "var(--brand-text-opacity-60)",
                          }}
                        >
                          Manual Attribution
                        </p>
                        <button
                          onClick={() => {
                            setShowManualAttribution(false);
                            setReferenceSearch("");
                            setSelectedReference(null);
                          }}
                          className="bg-transparent border-none cursor-pointer p-[2px]"
                          style={{
                            color:
                              "var(--brand-text-opacity-40)",
                          }}
                        >
                          <ChevronUp size={14} />
                        </button>
                      </div>

                      {/* Helper text */}
                      <div className="flex flex-col gap-[6px]">
                        <p
                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] m-0 leading-[1.5]"
                          style={{
                            color:
                              "var(--brand-text-opacity-50)",
                          }}
                        >
                          Select an already published Share
                          Board item in this competition.
                        </p>
                        <p
                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] m-0 leading-[1.5]"
                          style={{
                            color:
                              "var(--brand-text-opacity-50)",
                          }}
                        >
                          This will be shown as a
                          publisher-declared reference.
                        </p>
                        <p
                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] m-0 leading-[1.5]"
                          style={{
                            color:
                              "var(--brand-text-opacity-40)",
                          }}
                        >
                          It is not system-verified and will not
                          create a verified Share Board lineage.
                        </p>
                      </div>

                      {/* Selected reference card (if one is selected) */}
                      {selectedReference ? (
                        <div
                          className="flex items-center justify-between gap-[12px] p-[12px]"
                          style={{
                            backgroundColor: "var(--brand-bg)",
                            border: BORDER,
                          }}
                        >
                          <div className="flex items-center gap-[8px] flex-1 min-w-0">
                            <Check
                              size={14}
                              style={{
                                color: "#3B0E27",
                                flexShrink: 0,
                              }}
                            />
                            {selectedReference.publisherAvatar && (
                              <img
                                src={
                                  selectedReference.publisherAvatar
                                }
                                alt={
                                  selectedReference.publisherName
                                }
                                className="w-[20px] h-[20px] rounded-full object-cover shrink-0"
                                style={{
                                  border:
                                    "1px solid var(--brand-border)",
                                }}
                              />
                            )}
                            <div className="flex flex-col gap-[2px] min-w-0 flex-1">
                              <p
                                className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0 truncate"
                                style={{
                                  color: "var(--brand-text)",
                                }}
                              >
                                {
                                  selectedReference.cheatsheetTitle
                                }
                              </p>
                              <p
                                className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] m-0"
                                style={{
                                  color:
                                    "var(--brand-text-opacity-40)",
                                }}
                              >
                                {
                                  selectedReference.publisherName
                                }{" "}
                                ·{" "}
                                {formatDate(
                                  selectedReference.publishedAt,
                                )}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              setSelectedReference(null)
                            }
                            className="bg-transparent border-none cursor-pointer p-[4px]"
                            style={{
                              color:
                                "var(--brand-text-opacity-40)",
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        /* Search input and results */
                        <>
                          {/* Search input */}
                          <div
                            className="flex items-center gap-[8px] px-[10px] py-[8px]"
                            style={{
                              backgroundColor:
                                "var(--brand-bg)",
                              border: BORDER,
                            }}
                          >
                            <Search
                              size={14}
                              style={{
                                color:
                                  "var(--brand-text-opacity-30)",
                              }}
                            />
                            <input
                              type="text"
                              value={referenceSearch}
                              onChange={(e) =>
                                setReferenceSearch(
                                  e.target.value,
                                )
                              }
                              placeholder="Search published cheatsheets or paste a Share Board item link"
                              className="bg-transparent border-none outline-none flex-1 font-['Helvetica_Neue:Regular',sans-serif] text-[12px] min-w-0 placeholder:text-[rgba(59,14,39,0.3)]"
                              style={{
                                color: "var(--brand-text)",
                              }}
                            />
                            {referenceSearch && (
                              <button
                                onClick={() =>
                                  setReferenceSearch("")
                                }
                                className="bg-transparent border-none cursor-pointer p-0"
                              >
                                <X
                                  size={12}
                                  style={{
                                    color:
                                      "var(--brand-text-opacity-30)",
                                  }}
                                />
                              </button>
                            )}
                          </div>

                          {/* Search results */}
                          {availableReferences.length > 0 && (
                            <div className="flex flex-col gap-[6px] max-h-[200px] overflow-y-auto">
                              {availableReferences.map(
                                (item) => (
                                  <div
                                    key={item.id}
                                    className="flex items-center justify-between gap-[12px] p-[10px] cursor-pointer hover:bg-[rgba(59,14,39,0.02)]"
                                    style={{
                                      backgroundColor:
                                        "var(--brand-bg)",
                                      border: BORDER,
                                    }}
                                    onClick={() => {
                                      setSelectedReference(
                                        item,
                                      );
                                      setReferenceSearch("");
                                    }}
                                  >
                                    <div className="flex items-center gap-[8px] flex-1 min-w-0">
                                      {item.publisherAvatar && (
                                        <img
                                          src={
                                            item.publisherAvatar
                                          }
                                          alt={
                                            item.publisherName
                                          }
                                          className="w-[20px] h-[20px] rounded-full object-cover shrink-0"
                                          style={{
                                            border:
                                              "1px solid var(--brand-border)",
                                          }}
                                        />
                                      )}
                                      <div className="flex flex-col gap-[2px] min-w-0 flex-1">
                                        <p
                                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 truncate"
                                          style={{
                                            color:
                                              "var(--brand-text)",
                                          }}
                                        >
                                          {item.cheatsheetTitle}
                                        </p>
                                        <p
                                          className="font-['Helvetica_Neue:Regular',sans-serif] text-[10px] m-0"
                                          style={{
                                            color:
                                              "var(--brand-text-opacity-40)",
                                          }}
                                        >
                                          {item.publisherName} ·{" "}
                                          {formatDate(
                                            item.publishedAt,
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedReference(
                                          item,
                                        );
                                        setReferenceSearch("");
                                      }}
                                      className="flex items-center gap-[4px] bg-transparent cursor-pointer px-[10px] py-[4px]"
                                      style={{
                                        border: BORDER,
                                        color:
                                          "var(--brand-text-opacity-60)",
                                      }}
                                    >
                                      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px]">
                                        Select
                                      </span>
                                    </button>
                                  </div>
                                ),
                              )}
                            </div>
                          )}

                          {/* No results */}
                          {referenceSearch &&
                            availableReferences.length ===
                              0 && (
                              <p
                                className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 py-[8px] text-center"
                                style={{
                                  color:
                                    "var(--brand-text-opacity-40)",
                                }}
                              >
                                No published cheatsheets found
                              </p>
                            )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Publishing notices */}
          <div
            className="flex flex-col gap-[12px] p-[16px]"
            style={{
              backgroundColor: "var(--brand-surface-muted)",
              border: BORDER,
            }}
          >
            <p
              className="font-['Helvetica_Neue:Medium',sans-serif] text-[13px] m-0"
              style={{ color: "var(--brand-text)" }}
            >
              Publishing Notice
            </p>
            <div className="flex flex-col gap-[10px]">
              <div className="flex items-start gap-[8px]">
                <Info
                  size={14}
                  className="mt-[2px] shrink-0"
                  style={{
                    color: "var(--brand-text-opacity-40)",
                  }}
                />
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 leading-[1.5]"
                  style={{
                    color: "var(--brand-text-opacity-50)",
                  }}
                >
                  Your cheatsheet will be published on Share
                  Board and visible to all logged-in users.
                </p>
              </div>
              <div className="flex items-start gap-[8px]">
                <Info
                  size={14}
                  className="mt-[2px] shrink-0"
                  style={{
                    color: "var(--brand-text-opacity-40)",
                  }}
                />
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 leading-[1.5]"
                  style={{
                    color: "var(--brand-text-opacity-50)",
                  }}
                >
                  Other users will be able to see your avatar,
                  full name, published timestamp, and cheatsheet
                  content.
                </p>
              </div>
              <div className="flex items-start gap-[8px]">
                <Info
                  size={14}
                  className="mt-[2px] shrink-0"
                  style={{
                    color: "var(--brand-text-opacity-40)",
                  }}
                />
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 leading-[1.5]"
                  style={{
                    color: "var(--brand-text-opacity-50)",
                  }}
                >
                  Once published, the cheatsheet content cannot
                  be edited. If you delete and republish it, a
                  new published timestamp will be generated.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div
          className="flex items-center justify-end gap-[12px] px-[24px] py-[16px] shrink-0"
          style={{ borderTop: BORDER }}
        >
          <button
            onClick={onClose}
            className="px-[20px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] bg-transparent cursor-pointer transition-colors"
            style={{
              border: BORDER,
              color: "var(--brand-text-opacity-60)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePublish}
            disabled={!canPublish}
            className="px-[20px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] border-none cursor-pointer transition-colors"
            style={{
              backgroundColor: canPublish
                ? "var(--brand-primary-btn-bg)"
                : "var(--brand-surface-subtle)",
              color: canPublish
                ? "var(--brand-primary-btn-text)"
                : "var(--brand-text-opacity-30)",
              cursor: canPublish ? "pointer" : "not-allowed",
            }}
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Detail Modal ────────────────────────────────────── */

function DetailModal({
  item,
  liked,
  saved,
  onClose,
  onToggleLike,
  onToggleFavorite,
  onDelete,
}: {
  item: ShareItem;
  liked: boolean;
  saved: boolean;
  onClose: () => void;
  onToggleLike: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}) {
  return (
    <ModalOverlay onClose={onClose}>
      {/* Header */}
      <div
        className="flex items-start justify-between px-[24px] py-[20px] shrink-0"
        style={{ borderBottom: BORDER }}
      >
        <div className="flex flex-col gap-[8px]">
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[18px] m-0"
            style={{ color: "var(--brand-text)" }}
          >
            {item.cheatsheetTitle}
          </p>
          <div className="flex items-center gap-[12px]">
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0"
              style={{ color: "var(--brand-text-opacity-40)" }}
            >
              {item.competitionName}
            </p>
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0"
              style={{ color: "var(--brand-text-opacity-35)" }}
            >
              Published at {formatDate(item.publishedAt)}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="bg-transparent border-none cursor-pointer p-[4px] shrink-0"
        >
          <X
            size={18}
            style={{ color: "var(--brand-text-opacity-40)" }}
          />
        </button>
      </div>

      <div className="px-[24px] py-[20px] flex flex-col gap-[20px] overflow-y-auto flex-1 min-h-0">
        {/* Referenced from (if applicable) */}
        {item.referencedFrom && (
          <div
            className="flex items-center justify-between px-[16px] py-[12px]"
            style={{
              backgroundColor: "var(--brand-surface-subtle)",
              border: BORDER,
            }}
          >
            <div className="flex items-center gap-[10px] flex-1 min-w-0">
              <Repeat2
                size={16}
                style={{
                  color: "var(--brand-text-opacity-40)",
                  flexShrink: 0,
                }}
              />
              <div className="flex items-center gap-[8px] min-w-0 flex-1">
                {item.referencedFrom.originalAuthorAvatar && (
                  <img
                    src={
                      item.referencedFrom.originalAuthorAvatar
                    }
                    alt={item.referencedFrom.originalAuthor}
                    className="w-[18px] h-[18px] rounded-full object-cover shrink-0"
                    style={{
                      border: "1px solid var(--brand-border)",
                    }}
                  />
                )}
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0 truncate"
                  style={{
                    color: "var(--brand-text-opacity-60)",
                  }}
                >
                  Referenced from{" "}
                  <span style={{ color: "var(--brand-text)" }}>
                    {item.referencedFrom.originalTitle}
                  </span>{" "}
                  by {item.referencedFrom.originalAuthor}
                </p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                // TODO: Navigate to original item
              }}
              className="flex items-center gap-[5px] bg-transparent border-none cursor-pointer p-[6px_10px] shrink-0"
              style={{ color: "var(--brand-text-opacity-50)" }}
            >
              <ExternalLink size={13} />
              <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]">
                View Original
              </span>
            </button>
          </div>
        )}

        {/* Cheatsheet */}
        <div className="flex flex-col gap-[8px] flex-1 min-h-0">
          <div className="flex items-center gap-[6px]">
            <FileText
              size={14}
              style={{ color: "var(--brand-text-opacity-40)" }}
            />
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0"
              style={{ color: "var(--brand-text-opacity-60)" }}
            >
              Cheatsheet
            </p>
          </div>
          <div
            className="p-[16px] font-['Menlo,Monaco,monospace'] text-[13px] whitespace-pre-wrap break-words leading-[1.7] flex-1 min-h-0 overflow-y-auto"
            style={{
              backgroundColor: "var(--brand-surface-muted)",
              border: BORDER,
              color: "var(--brand-text)",
            }}
          >
            {item.cheatsheetContent}
          </div>
        </div>
      </div>

      {/* Author actions */}
      <div
        className="flex items-center justify-between px-[24px] py-[16px] shrink-0"
        style={{ borderTop: BORDER }}
      >
        <div className="flex items-center gap-[8px]">
          {item.isOwner && (
            <button
              onClick={onDelete}
              className="flex items-center gap-[6px] px-[14px] py-[7px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] bg-transparent cursor-pointer transition-colors"
              style={{
                border: "1px solid rgba(198, 40, 40, 0.3)",
                color: "#c62828",
              }}
            >
              <Trash2 size={13} />
              Delete
            </button>
          )}
        </div>
        <div className="flex items-center gap-[2px]">
          <ReferenceButton
            referenced={liked}
            count={item.referenceCount}
            onToggle={onToggleLike}
            size="md"
          />
          <FavoriteButton
            saved={saved}
            count={item.favoriteCount}
            onToggle={onToggleFavorite}
            size="md"
          />
        </div>
      </div>
    </ModalOverlay>
  );
}

/* ── Delete Confirmation Modal ───────────────────────── */

function DeleteModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-[24px]"
      style={{ backgroundColor: "var(--brand-overlay)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] flex flex-col"
        style={{
          backgroundColor: "var(--brand-bg)",
          border: BORDER,
        }}
      >
        <div className="px-[24px] py-[28px] flex flex-col gap-[16px]">
          <div className="flex items-center gap-[10px]">
            <AlertTriangle
              size={20}
              style={{ color: "#c62828" }}
            />
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[18px] m-0"
              style={{ color: "var(--brand-text)" }}
            >
              Delete this published cheatsheet?
            </p>
          </div>
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0 leading-[1.6]"
            style={{ color: "var(--brand-text-opacity-60)" }}
          >
            This published item will be removed from the Share
            Board and will no longer be visible to users.
          </p>
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0"
            style={{ color: "var(--brand-text-opacity-40)" }}
          >
            This action cannot be undone from the frontend.
          </p>
        </div>
        <div
          className="flex items-center justify-end gap-[12px] px-[24px] py-[16px]"
          style={{ borderTop: BORDER }}
        >
          <button
            onClick={onClose}
            className="px-[20px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] bg-transparent cursor-pointer"
            style={{
              border: BORDER,
              color: "var(--brand-text-opacity-60)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-[20px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] border-none cursor-pointer"
            style={{
              backgroundColor: "#c62828",
              color: "#ffffff",
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Share Item Card ─────────────────────────────────── */

function ShareItemCard({
  item,
  liked,
  saved,
  onToggleLike,
  onToggleFavorite,
  onClick,
  onDelete,
}: {
  item: ShareItem;
  liked: boolean;
  saved: boolean;
  onToggleLike: () => void;
  onToggleFavorite: () => void;
  onClick: () => void;
  onDelete: () => void;
}) {
  const preview = truncateLines(item.cheatsheetContent, 5);

  return (
    <div
      className="flex flex-col cursor-pointer transition-colors hover:bg-[rgba(59,14,39,0.02)]"
      style={{ border: BORDER }}
      onClick={onClick}
    >
      {/* Header: title + time */}
      <div className="px-[20px] pt-[18px] pb-[12px] flex flex-col gap-[8px]">
        {/* Top row: publisher info + date */}
        <div className="flex items-center justify-between gap-[12px]">
          <div className="flex items-center gap-[8px]">
            {item.publisherAvatar ? (
              <img
                src={item.publisherAvatar}
                alt={item.publisherName}
                className="w-[20px] h-[20px] rounded-full object-cover shrink-0"
                style={{
                  border: "1px solid var(--brand-border)",
                }}
              />
            ) : (
              <div
                className="w-[20px] h-[20px] rounded-full flex items-center justify-center shrink-0 font-['Helvetica_Neue:Regular',sans-serif] text-[10px]"
                style={{
                  backgroundColor: "var(--brand-surface-muted)",
                  border: "1px solid var(--brand-border)",
                  color: "var(--brand-text-opacity-50)",
                }}
              >
                {item.publisherName.charAt(0).toUpperCase()}
              </div>
            )}
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0"
              style={{ color: "var(--brand-text-opacity-50)" }}
            >
              {item.publisherName}
            </p>
          </div>
          <p
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 whitespace-nowrap shrink-0"
            style={{ color: "var(--brand-text-opacity-35)" }}
          >
            {formatDate(item.publishedAt)}
          </p>
        </div>

        {/* Title */}
        <p
          className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] m-0"
          style={{ color: "var(--brand-text)" }}
        >
          {item.cheatsheetTitle}
        </p>

        {/* Competition type */}
        {/* <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0" style={{ color: "var(--brand-text-opacity-40)" }}>
          {item.competitionName}
        </p> */}
      </div>

      {/* Cheatsheet preview */}
      <div className="px-[20px] py-[0px]">
        <div
          className="relative font-['Menlo,Monaco,monospace'] whitespace-pre-wrap break-words leading-[1.65] text-[14px] h-[166px] overflow-hidden"
          style={{
            backgroundColor: "var(--brand-surface-muted)",
            border: BORDER,
            color: "var(--brand-text-opacity-60)",
          }}
        >
          <div className="p-[14px]">
            {preview.text}
            {preview.truncated && (
              <span
                style={{
                  color: "var(--brand-text-opacity-30)",
                }}
              >
                {"\n..."}
              </span>
            )}
          </div>

          {/* Referenced from bar - positioned at bottom with 0px gap */}
          {item.referencedFrom && (
            <div
              className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-[12px] h-[48px]"
              style={{
                backgroundColor: "var(--brand-surface-subtle)",
                borderTop: BORDER,
              }}
            >
              <div className="flex items-center gap-[10px] flex-1 min-w-0">
                <Repeat2
                  size={14}
                  style={{
                    color: "var(--brand-text-opacity-40)",
                    flexShrink: 0,
                  }}
                />
                <div className="flex items-center gap-[6px] min-w-0 flex-1">
                  {item.referencedFrom.originalAuthorAvatar && (
                    <img
                      src={
                        item.referencedFrom.originalAuthorAvatar
                      }
                      alt={item.referencedFrom.originalAuthor}
                      className="w-[16px] h-[16px] rounded-full object-cover shrink-0"
                      style={{
                        border: "1px solid var(--brand-border)",
                      }}
                    />
                  )}
                  <p
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 truncate"
                    style={{
                      color: "var(--brand-text-opacity-50)",
                    }}
                  >
                    Referenced from{" "}
                    <span
                      style={{
                        color: "var(--brand-text-opacity-70)",
                      }}
                    >
                      {item.referencedFrom.originalTitle}
                    </span>{" "}
                    by {item.referencedFrom.originalAuthor}
                  </p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Navigate to original item
                  onClick();
                }}
                className="flex items-center gap-[4px] bg-transparent border-none cursor-pointer p-[4px_8px] shrink-0"
                style={{
                  color: "var(--brand-text-opacity-50)",
                }}
              >
                <ExternalLink size={12} />
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px]">
                  View
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Actions row: View Details left, Like/Favorite right */}
      <div
        className="px-[20px] py-[10px] flex items-center justify-between mt-auto"
        style={{ borderColor: "var(--brand-border)" }}
      >
        <div className="flex items-center gap-[8px]">
          {item.isOwner && null}
        </div>
        <div className="flex items-center gap-[2px]">
          <ReferenceButton
            referenced={liked}
            count={item.referenceCount}
            onToggle={onToggleLike}
          />
          <FavoriteButton
            saved={saved}
            count={item.favoriteCount}
            onToggle={onToggleFavorite}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Filter Dropdown ─────────────────────────────────── */

function FilterDropdown({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () =>
      document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-[8px] px-[12px] py-[8px] bg-transparent cursor-pointer font-['Helvetica_Neue:Regular',sans-serif] text-[14px]"
        style={{
          border: BORDER,
          color: "var(--brand-text-opacity-60)",
        }}
      >
        {value}
        <ChevronDown
          size={14}
          style={{ color: "var(--brand-text-opacity-30)" }}
        />
      </button>
      {open && (
        <div
          className="absolute top-[calc(100%+4px)] left-0 min-w-[160px] z-50 py-[4px]"
          style={{
            backgroundColor: "var(--brand-bg)",
            border: BORDER,
            boxShadow: "0 4px 16px var(--brand-shadow)",
          }}
        >
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="w-full bg-transparent border-none cursor-pointer text-left px-[14px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] transition-colors hover:bg-[rgba(59,14,39,0.03)]"
              style={{
                color:
                  opt === value
                    ? "var(--brand-text)"
                    : "var(--brand-text-opacity-60)",
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Component ──────────────────────────────────── */

export default function ShareBoard({
  openPublish = false,
  onPublishModalClose,
  openItemId,
}: {
  openPublish?: boolean;
  onPublishModalClose?: () => void;
  openItemId?: string;
}) {
  const [items, setItems] = useState<ShareItem[]>(MOCK_ITEMS);
  const [searchQuery, setSearchQuery] = useState("");
  const [competitionFilter, setCompetitionFilter] = useState(
    "All Competitions",
  );
  const [sortOrder, setSortOrder] = useState("Latest");
  const [activeTab, setActiveTab] = useState<
    "all" | "my" | "favorites"
  >("all");

  // Interaction state
  const [likedIds, setLikedIds] = useState<Set<string>>(
    new Set(),
  );
  const [savedIds, setSavedIds] = useState<Set<string>>(
    new Set(),
  );

  // Modal state
  const [showPublishModal, setShowPublishModal] =
    useState(openPublish);
  const [detailItem, setDetailItem] =
    useState<ShareItem | null>(null);
  const [deleteItem, setDeleteItem] =
    useState<ShareItem | null>(null);

  // Auto-open item detail if openItemId is provided
  useEffect(() => {
    if (openItemId) {
      const item = items.find((i) => i.id === openItemId);
      if (item) {
        setDetailItem(item);
      }
    }
  }, [openItemId, items]);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setItems((items) =>
          items.map((i) =>
            i.id === id
              ? { ...i, referenceCount: i.referenceCount - 1 }
              : i,
          ),
        );
      } else {
        next.add(id);
        setItems((items) =>
          items.map((i) =>
            i.id === id
              ? { ...i, referenceCount: i.referenceCount + 1 }
              : i,
          ),
        );
      }
      return next;
    });
  };

  const toggleFavorite = (id: string) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setItems((items) =>
          items.map((i) =>
            i.id === id
              ? { ...i, favoriteCount: i.favoriteCount - 1 }
              : i,
          ),
        );
      } else {
        next.add(id);
        setItems((items) =>
          items.map((i) =>
            i.id === id
              ? { ...i, favoriteCount: i.favoriteCount + 1 }
              : i,
          ),
        );
      }
      return next;
    });
  };

  const filteredItems = useMemo(() => {
    let result = [...items];

    // Tab filter
    if (activeTab === "my") {
      result = result.filter((i) => i.isOwner);
    } else if (activeTab === "favorites") {
      result = result.filter((i) => savedIds.has(i.id));
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (i) =>
          i.cheatsheetTitle.toLowerCase().includes(q) ||
          i.cheatsheetContent.toLowerCase().includes(q),
      );
    }

    // Competition
    if (competitionFilter !== "All Competitions") {
      result = result.filter(
        (i) => i.competitionName === competitionFilter,
      );
    }

    // Sort
    result.sort((a, b) => {
      const da = new Date(a.publishedAt).getTime();
      const db = new Date(b.publishedAt).getTime();
      return sortOrder === "Latest" ? db - da : da - db;
    });

    return result;
  }, [
    items,
    searchQuery,
    competitionFilter,
    sortOrder,
    activeTab,
    savedIds,
  ]);

  const handlePublish = (title: string, content: string) => {
    const newItem: ShareItem = {
      id: String(Date.now()),
      cheatsheetTitle: title,
      cheatsheetContent: content,
      competitionName: "Mathematics Distillation v1",
      publishedAt: new Date().toISOString(),
      referenceCount: 0,
      favoriteCount: 0,
      isOwner: true,
      publisherName: "Alice Johnson",
      publisherAvatar:
        "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    };
    setItems((prev) => [newItem, ...prev]);
    setShowPublishModal(false);
    if (onPublishModalClose) onPublishModalClose();
  };

  const handleDelete = () => {
    if (!deleteItem) return;
    setItems((prev) =>
      prev.filter((i) => i.id !== deleteItem.id),
    );
    setDeleteItem(null);
    setDetailItem(null);
  };

  return (
    <div
      className="w-full"
      style={{ backgroundColor: "var(--brand-bg)" }}
    >
      <div className="max-w-[1920px] mx-auto px-[24px] py-[36px]">
        {/* Page Header */}
        <div
          className="flex flex-col md:flex-row gap-[24px] mb-[32px] p-[32px]"
          style={{
            height: "360px",
            border: BORDER,
            backgroundColor: "var(--brand-surface-subtle)",
          }}
        >
          {/* Left: Content + Button */}
          <div className="flex flex-col justify-center gap-[20px] flex-1">
            <div className="flex flex-col gap-[12px]">
              <h1
                className="font-['Helvetica_Neue:Regular',sans-serif] m-0 font-[Helvetica_Neue] text-[48px]"
                style={{ color: "var(--brand-text)" }}
              >
                Share Board
              </h1>
              <p
                className="font-['Helvetica_Neue:Regular',sans-serif] m-0 leading-[1.6] max-w-[480px] text-[16px]"
                style={{
                  color: "var(--brand-text-opacity-60)",
                }}
              >
                A public board for published cheatsheets. This
                is not the official competition leaderboard.
              </p>
            </div>
            <button
              onClick={() => setShowPublishModal(true)}
              className="self-start px-[28px] py-[11px] font-['Helvetica_Neue:Regular',sans-serif] border-none cursor-pointer transition-colors hover:bg-[#4D1233] text-[16px]"
              style={{
                backgroundColor: "var(--brand-primary-btn-bg)",
                color: "var(--brand-primary-btn-text)",
              }}
            >
              Publish Cheatsheet
            </button>
          </div>

          {/* Right: Image Placeholder */}
          <div
            className="hidden md:flex items-center justify-center flex-1"
            style={{
              backgroundColor: "var(--brand-surface-muted)",
              border: BORDER,
            }}
          ></div>
        </div>

        {/* Tab Navigation */}
        <div
          className="flex items-center gap-[24px] mb-[24px]"
          style={{ borderBottom: BORDER }}
        >
          <button
            onClick={() => setActiveTab("all")}
            className="relative bg-transparent border-none cursor-pointer px-0 pb-[12px] font-['Helvetica_Neue:Regular',sans-serif] transition-colors text-[16px]"
            style={{
              color:
                activeTab === "all"
                  ? "var(--brand-text)"
                  : "var(--brand-text-opacity-40)",
            }}
          >
            All Cheatsheets
            {activeTab === "all" && (
              <div
                className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{ backgroundColor: "var(--brand-text)" }}
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab("my")}
            className="relative bg-transparent border-none cursor-pointer px-0 pb-[12px] font-['Helvetica_Neue:Regular',sans-serif] transition-colors text-[16px]"
            style={{
              color:
                activeTab === "my"
                  ? "var(--brand-text)"
                  : "var(--brand-text-opacity-40)",
            }}
          >
            My Published
            {activeTab === "my" && (
              <div
                className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{ backgroundColor: "var(--brand-text)" }}
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab("favorites")}
            className="relative bg-transparent border-none cursor-pointer px-0 pb-[12px] font-['Helvetica_Neue:Regular',sans-serif] text-[15px] flex items-center gap-[6px] transition-colors"
            style={{
              color:
                activeTab === "favorites"
                  ? "var(--brand-text)"
                  : "var(--brand-text-opacity-40)",
            }}
          >
            <span className="text-[16px]">My Favorites</span>
            {savedIds.size > 0 && (
              <span
                className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] px-[6px] py-[1px] rounded-full"
                style={{
                  backgroundColor: "var(--brand-surface-muted)",
                  color: "var(--brand-text-opacity-50)",
                }}
              >
                {savedIds.size}
              </span>
            )}
            {activeTab === "favorites" && (
              <div
                className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{ backgroundColor: "var(--brand-text)" }}
              />
            )}
          </button>
        </div>

        {/* Filter Bar */}
        <div
          className="flex flex-col md:flex-row md:items-center gap-[12px] mb-[24px] pb-[20px]"
          style={{ borderBottom: BORDER }}
        >
          {/* Search */}
          <div
            className="flex items-center gap-[8px] px-[12px] py-[8px] flex-1 min-w-0"
            style={{ border: BORDER }}
          >
            <Search
              size={15}
              style={{ color: "var(--brand-text-opacity-30)" }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title or content..."
              className="bg-transparent border-none outline-none flex-1 font-['Helvetica_Neue:Regular',sans-serif] text-[13px] min-w-0 placeholder:text-[rgba(59,14,39,0.3)]"
              style={{ color: "var(--brand-text)" }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="bg-transparent border-none cursor-pointer p-0"
              >
                <X
                  size={14}
                  style={{
                    color: "var(--brand-text-opacity-30)",
                  }}
                />
              </button>
            )}
          </div>

          <div className="flex items-center gap-[8px] flex-wrap">
            {/* Competition */}
            <FilterDropdown
              value={competitionFilter}
              options={COMPETITIONS}
              onChange={setCompetitionFilter}
            />

            {/* Sort */}
            <FilterDropdown
              value={sortOrder}
              options={["Latest", "Earliest"]}
              onChange={setSortOrder}
            />
          </div>
        </div>

        {/* Results count */}
        <p
          className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 mb-[16px]"
          style={{ color: "var(--brand-text-opacity-35)" }}
        >
          {filteredItems.length}{" "}
          {filteredItems.length === 1 ? "item" : "items"}
        </p>

        {/* Card List */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 min-[1200px]:grid-cols-2 min-[1920px]:grid-cols-3 gap-[16px]">
            {filteredItems.map((item) => (
              <ShareItemCard
                key={item.id}
                item={item}
                liked={likedIds.has(item.id)}
                saved={savedIds.has(item.id)}
                onToggleLike={() => toggleLike(item.id)}
                onToggleFavorite={() => toggleFavorite(item.id)}
                onClick={() => setDetailItem(item)}
                onDelete={() => setDeleteItem(item)}
              />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div
            className="flex flex-col items-center justify-center py-[80px] gap-[16px]"
            style={{ border: BORDER }}
          >
            <div
              className="size-[56px] flex items-center justify-center"
              style={{
                backgroundColor: "var(--brand-surface-muted)",
              }}
            >
              <FileText
                size={24}
                style={{
                  color: "var(--brand-text-opacity-30)",
                }}
              />
            </div>
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] m-0"
              style={{ color: "var(--brand-text)" }}
            >
              No public cheatsheets yet
            </p>
            <p
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0"
              style={{ color: "var(--brand-text-opacity-40)" }}
            >
              There are no published cheatsheets on the Share
              Board yet.
            </p>
            <button
              onClick={() => setShowPublishModal(true)}
              className="mt-[8px] px-[20px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] border-none cursor-pointer hover:bg-[#4D1233]"
              style={{
                backgroundColor: "var(--brand-primary-btn-bg)",
                color: "var(--brand-primary-btn-text)",
              }}
            >
              Be the first to publish one
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPublishModal && (
        <PublishModal
          onClose={() => {
            setShowPublishModal(false);
            if (onPublishModalClose) onPublishModalClose();
          }}
          onPublish={handlePublish}
        />
      )}

      {detailItem && !deleteItem && (
        <DetailModal
          item={detailItem}
          liked={likedIds.has(detailItem.id)}
          saved={savedIds.has(detailItem.id)}
          onClose={() => setDetailItem(null)}
          onToggleLike={() => toggleLike(detailItem.id)}
          onToggleFavorite={() => toggleFavorite(detailItem.id)}
          onDelete={() => setDeleteItem(detailItem)}
        />
      )}

      {deleteItem && (
        <DeleteModal
          onClose={() => setDeleteItem(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}